import { JobApplication, JobApplicationStatusKey } from '@restworld/utility-types';

/**
 * Single source of truth for "which job applications does the employer actually see on the
 * job position page". Both the shortlist section and the kanban board build their rows from
 * these lists, and the preselection survey uses them to decide whether the board is empty.
 *
 * Keeping them here rather than inline in each component matters because the survey's whole
 * display rule is "don't interrupt someone who has candidates to look at": if this list ever
 * drifts from what the UI renders, the survey starts appearing over a populated board.
 */

/** Rendered by the shortlist section — candidates waiting for a first look. */
export const SHORTLIST_STATUS_KEYS: JobApplicationStatusKey[] = [
  'worker_jp_in_shortlist',
  'worker_jp_viewed_by_employer',
];

/** Kanban row: "to be called". */
export const KANBAN_INTERESTED_STATUS_KEYS: JobApplicationStatusKey[] = ['worker_jp_employer_interested'];

/** Kanban row: "to be interviewed". */
export const KANBAN_INTERVIEW_STATUS_KEYS: JobApplicationStatusKey[] = [
  'worker_jp_interview_scheduled',
  'worker_jp_interview_done',
];

/** Kanban row: "negotiation". */
export const KANBAN_STAGE_STATUS_KEYS: JobApplicationStatusKey[] = [
  'worker_jp_stage_scheduled',
  'worker_jp_stage',
  'worker_jp_stage_done',
];

/** Kanban row: "to be hired". */
export const KANBAN_HIRED_STATUS_KEYS: JobApplicationStatusKey[] = ['worker_jp_hired', 'worker_jp_scheduled_hiring'];

/**
 * Everything the employer can see without opening a secondary modal.
 *
 * Deliberately excludes `worker_jp_proposed` and `worker_jp_interested`: those are proposals
 * sent to workers, created within seconds of publication by the backend's async
 * `propose_workers` task, and invisible to the company. Also excludes `worker_jp_discarded`
 * and the "not interested / not selected" states, which only surface inside their own modals.
 */
export const VISIBLE_CANDIDATE_STATUS_KEYS: JobApplicationStatusKey[] = [
  ...SHORTLIST_STATUS_KEYS,
  ...KANBAN_INTERESTED_STATUS_KEYS,
  ...KANBAN_INTERVIEW_STATUS_KEYS,
  ...KANBAN_STAGE_STATUS_KEYS,
  ...KANBAN_HIRED_STATUS_KEYS,
];

export const hasVisibleCandidates = (jobApplications: Pick<JobApplication, 'status_key'>[] | undefined) =>
  Boolean(jobApplications?.some(({ status_key }) => VISIBLE_CANDIDATE_STATUS_KEYS.includes(status_key)));

/**
 * The id of a job application as the backend knows it: `worker_id + job_position_id`, or
 * `worker_id + restaurant_id` for a spontaneous application.
 *
 * Built from its parts instead of reading `id`, because the kanban board spreads the row id over
 * the application (`{ ...jobApplication, id }` in candidate-kanban-board.tsx), so the object that
 * reaches the candidate modal can carry `id: 'interested'`. The discard flow rebuilds it the same way.
 */
export const getJobApplicationId = (
  jobApplication: Partial<Pick<JobApplication, 'worker_id' | 'job_position_id' | 'restaurant_id'>> | undefined
): string | undefined => {
  if (!jobApplication?.worker_id) return undefined;
  const suffix = jobApplication.job_position_id ?? jobApplication.restaurant_id;
  return suffix ? `${jobApplication.worker_id}${suffix}` : undefined;
};

/** Stages from which the employer has actually dealt with the candidate, so a report makes sense. */
export const REPORTABLE_STATUS_KEYS: JobApplicationStatusKey[] = [
  ...KANBAN_INTERESTED_STATUS_KEYS,
  ...KANBAN_INTERVIEW_STATUS_KEYS,
  ...KANBAN_STAGE_STATUS_KEYS,
  ...KANBAN_HIRED_STATUS_KEYS,
];

/**
 * Whether the employer can report this candidate ("Segnala" link in the candidate modal).
 *
 * The same rule lives in the backend in `RestWorld.WorkerJobPositions.EmployerReports.reportable?/1`:
 * if one changes, the other must change too, or the link shows and the POST answers 422.
 */
export const isReportableJobApplication = (
  jobApplication:
    | Partial<Pick<JobApplication, 'status_key' | 'discarded_from_status_key' | 'job_position_id'>>
    | undefined
): boolean => {
  if (!jobApplication?.status_key) return false;
  const { status_key, discarded_from_status_key, job_position_id } = jobApplication;

  // Spontaneous application: anything the employer can see, i.e. not a proposal sent to the worker.
  if (!job_position_id) return status_key !== 'worker_jp_proposed' && status_key !== 'worker_jp_interested';

  if (REPORTABLE_STATUS_KEYS.includes(status_key)) return true;
  return (
    status_key === 'worker_jp_discarded' &&
    !!discarded_from_status_key &&
    REPORTABLE_STATUS_KEYS.includes(discarded_from_status_key)
  );
};

/**
 * Whether the candidate modal hides its action bar ("Mi interessa", "Sposta in", "Nascondi").
 *
 * Only a worker who withdrew before reaching the shortlist loses it: there is nothing the
 * employer can do with an application the worker pulled. A "non idoneo" discarded by the AI
 * or by a CSM keeps the bar, because recovering them into the pipeline is the whole point of
 * that tab (introduced on 28/05/2026, lost in the 16/09 refactor, restored on 01/10/2026).
 */
export const hidesCandidateActionBar = (
  jobApplication: Partial<Pick<JobApplication, 'status_key' | 'added_to_shortlist_at'>> | undefined | null
) => !jobApplication?.added_to_shortlist_at && jobApplication?.status_key === 'worker_jp_not_interested';
